import React, { useState, useEffect } from 'react';
import { useSpring, animated } from '@react-spring/web';
import { useInView } from 'react-intersection-observer';

/**
 * A counter that animates in two distinct phases.
 *
 * @param {number} from - The number to start counting from.
 * @param {number} to - The intermediate number to "rush" to.
 * @param {number} duration - The duration of the initial rush in seconds.
 * @param {number} slowIncrement - The amount to slowly add after the rush.
 * @param {number} slowDuration - The duration of the slow increment in seconds.
 */
const Counter = ({
  from,
  to,
  duration = 2,
  slowIncrement = 10,
  slowDuration = 7,
}) => {
  const [ref, inView] = useInView({
    threshold: 0.3,
    triggerOnce: false, // Set to true if you only want it to run once
  });

  // This state tracks if we have finished the initial rush and should start the slow tick.
  const [isTicking, setIsTicking] = useState(false);

  // The final number the animation will end on.
  const finalValue = to + slowIncrement;

  // --- PHASE 1: The fast rush animation (from -> to) ---
  const { number: rushNumber } = useSpring({
    from: { number: from },
    to: inView ? to : from,
    config: { duration: duration * 1000 },
    // When this animation finishes, we trigger the next phase if the component is still visible.
    onRest: () => {
      if (inView) {
        setIsTicking(true);
      }
    },
  });

  // --- PHASE 2: The slow tick-up animation (to -> to + slowIncrement) ---
  const { number: tickNumber } = useSpring({
    from: { number: to },
    to: isTicking ? finalValue : to,
    config: { duration: slowDuration * 1000 },
  });

  // This effect resets the state when the component scrolls out of view.
  useEffect(() => {
    if (!inView) {
      setIsTicking(false);
    }
  }, [inView]);

  // We decide which animated number to display based on the current phase.
  const displayValue = isTicking ? tickNumber : rushNumber;

  return (
    <animated.span ref={ref}>
      {displayValue.to(n => Math.floor(n).toLocaleString())}
    </animated.span>
  );
};

export default Counter;